import React, {
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { formatDate, createSanitizedMarkup } from '@fiverr-private/futile';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Heading, Text } from '@fiverr-private/typography';
import { theme } from '@fiverr-private/theme';
import { I18n } from '@fiverr-private/i18n-react';
import { CheckStarSolidIcon } from '@fiverr-private/icons';
import { bigQuery } from '@fiverr-private/obs';
import { useLatest } from '@fiverr-private/hooks';
import { APP_KEY } from '../../constants';
import { useEnteredViewOnce } from '../../hooks/useEnteredViewOnce';
import { bqTestimonialsCardIsShown } from '../../events/bigQuery/testimonialsCardIsShown';
import { Quote } from './assets/Quote';
import { textClassName, quoteClassName } from './CertifiedTestimonial.css';

const SECOND_IN_MS = 1000;

export const T_KEY = `${APP_KEY}.certified_testimonial`;

export const TESTIMONIAL_DATE_FORMAT: Parameters<typeof formatDate>['1'] = {
  month: '2-digit',
  day: '2-digit',
};

export interface CertifiedTestimonialProps
  extends ComponentProps<typeof Stack> {
  className?: string;
  withCompanySpace?: boolean;
  approveIcon?: ReactNode;
  id: string;
  text: string | null;
  client: {
    name: string;
    role: string;
    company: string | null;
  };
  project: {
    date: number;
    name: string;
  };
  /**
   * BI params, if provided, Impression event will be sent when the testimonial is shown.
   * otherwise, no event will be sent.
   */
  biParams?: {
    pageName: string;
    sellerId: string;
    certifiedPartner?: {
      id: string;
      name: string;
    };
  };
}

export const CertifiedTestimonial: FC<CertifiedTestimonialProps> = ({
  id,
  project: { name: projectName, date },
  text,
  approveIcon,
  client: { name: clientName, company, role: clientRole },
  className,
  withCompanySpace,
  biParams,
  ...rest
}) => {
  const observableRef = useRef<HTMLDivElement>(null);
  // make it possible for the callback to be independent of the biParams prop
  const biParamsRef = useLatest(biParams);

  const sendImpressionEvent = useCallback(() => {
    if (!biParamsRef.current) {
      return;
    }

    bigQuery.send(
      bqTestimonialsCardIsShown({
        ...biParamsRef.current,
        testimonialId: id,
      })
    );
  }, [id, biParamsRef]);

  useEnteredViewOnce(observableRef, sendImpressionEvent);

  return (
    <Stack
      ref={observableRef}
      direction="column"
      borderColor="border"
      borderWidth="sm"
      borderRadius="lg"
      padding="8"
      position="relative"
      gap="4"
      backgroundColor="white"
      className={className}
      {...rest}
    >
      <Container>
        <Quote className={quoteClassName} />
        <Heading as="h5">{projectName}</Heading>

        {company && (
          <Heading as="h6" marginTop="1">
            <I18n k={`${T_KEY}.company`} params={{ company }} />
          </Heading>
        )}
        {/* <Container /> as a placeholder, to maintain text alignment on all cards */}
        {withCompanySpace && !company && (
          <Container
            minHeight={{
              default: theme.headingLineHeights.h_xxs,
              sm: theme.headingLineHeights.h_xs,
            }}
          />
        )}
      </Container>
      {text && (
        <Text
          fontFamily="domaine"
          size="b_lg"
          overflow="hidden"
          textOverflow="ellipsis"
          className={textClassName}
          dangerouslySetInnerHTML={createSanitizedMarkup(`"${text}"`)}
        />
      )}
      <Container>
        <Text size="b_sm" fontWeight="bold">
          {clientName} | {clientRole}
        </Text>
        <Stack gap="3">
          <Text size="b_sm">
            {formatDate(new Date(date * SECOND_IN_MS), TESTIMONIAL_DATE_FORMAT)}
          </Text>
          <Stack width="auto" gap="1" alignItems="center">
            {approveIcon || (
              <CheckStarSolidIcon
                size={12}
                color={theme.colors[theme.bodyColors.bodyPrimary]}
              />
            )}
            <Text size="b_sm" color="bodySecondary">
              <I18n k={`${T_KEY}.approved`} />
            </Text>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};
